import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAttendanceRulesEntry } from './hooks/useAttendanceRulesEntry';

export default function AttendanceRulesEntryScreen() {
  const { state, actions } = useAttendanceRulesEntry();
  const navigate = useNavigate();
  const { form, isEdit, isLoading, isSaving } = state;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center p-16">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Attendance Rule Details...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Attendance Rule' : 'Create Attendance Rule'}
          </h1>
        </div>

        <Link
          to="/payroll/attendance-rules"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout matching Screenshot 2 / Salary Process layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Main Form Card (lg:col-span-8) matching Screenshot 2 */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden">
          {/* Card Header Bar */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">Attendance Rules</h2>
          </div>

          {/* Form Body matching Screenshot 2 */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-4">
              {/* Rule Name */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Rule Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={form.ruleName}
                  onChange={(e) => actions.patchForm('ruleName', e.target.value)}
                  placeholder="e.g. Generic"
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Alias */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Alias
                </label>
                <input
                  type="text"
                  value={form.alias}
                  onChange={(e) => actions.patchForm('alias', e.target.value)}
                  placeholder="e.g. GEN"
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* SwipeTimeAfter (Minitues) */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  SwipeTimeAfter(Minitues)
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.swipeTimeAfter}
                  onChange={(e) => actions.patchForm('swipeTimeAfter', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* SwipeTimeBefore (Minitues) */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  SwipeTimeBefore(Minitues)
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.swipeTimeBefore}
                  onChange={(e) => actions.patchForm('swipeTimeBefore', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* LateHalfDayDeduct (Minitues) */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  LateHalfDayDeduct(Minitues)
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.lateHalfDayDeduct}
                  onChange={(e) => actions.patchForm('lateHalfDayDeduct', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* EarlyHalfDayDeduct (Minitues) */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  EarlyHalfDayDeduct(Minitues)
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.earlyHalfDayDeduct}
                  onChange={(e) => actions.patchForm('earlyHalfDayDeduct', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* No Of Late/Early Marks */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  No Of Late/Early Marks
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.noOfLateEarlyMarks}
                  onChange={(e) => actions.patchForm('noOfLateEarlyMarks', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Late/Early DaysDeduct */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Late/Early DaysDeduct
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.5"
                  value={form.lateEarlyDaysDeduct}
                  onChange={(e) => actions.patchForm('lateEarlyDaysDeduct', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Sandwitch Present Toggle Switch */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Sandwitch Present
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => actions.patchForm('sandwitchPresent', !form.sandwitchPresent)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      form.sandwitchPresent ? 'bg-primary' : 'bg-outline-variant'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-surface shadow-md ring-0 transition duration-200 ease-in-out ${
                        form.sandwitchPresent ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-xs font-medium text-on-surface-variant">
                    {form.sandwitchPresent ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons Row matching Screenshot 2 */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isSaving}
              onClick={() => actions.handleSave({ exitAfter: true })}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">save</span>
              <span>{isSaving ? 'Saving...' : 'save & eXit'}</span>
            </button>

            {!isEdit && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => actions.handleSave({ exitAfter: false })}
                className="h-9 px-5 bg-surface-container-lowest hover:bg-surface-container-low text-primary font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/40 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Save &amp; continue
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/payroll/attendance-rules')}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Written Context / TIPS Panel matching Screenshot 2 (Salary Process layout) */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500 font-bold">
              lightbulb
            </span>
            <span>TIPS:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Attendance Rules establish swipe window tolerances, late mark thresholds, and half-day deduction policies for daily attendance execution.
          </p>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              POLICY GUIDELINES:
            </h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>
                <strong className="text-on-surface">Swipe Time Tolerances:</strong> SwipeTimeAfter/Before define the allowed window in minutes around shift punch times.
              </li>
              <li>
                <strong className="text-on-surface">Half-Day Deduction:</strong> LateHalfDayDeduct and EarlyHalfDayDeduct set thresholds triggering half-day deductions.
              </li>
              <li>
                <strong className="text-on-surface">Late Marks:</strong> Accumulating specified late marks automatically triggers leave or pay deductions.
              </li>
              <li>
                <strong className="text-on-surface">Sandwich Rule:</strong> When enabled, weekends or holidays between unapproved leave days are marked absent.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
